import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Formik, useField, ErrorMessage } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { PaperPlaneIcon, AviveLoadingIcon } from '@rsos/assets/icons';
import Box from '@rsos/components/capstone/base/Box';
import Button from '@rsos/components/capstone/base/Button/Button';
import {
  StyledAviveSearchForm,
  StyledHeader,
  StyledForm,
  StyledFormGroup,
  StyledTextArea,
  ButtonBox,
  Text as BaseText,
} from './AviveSearchForm.styles';
import { Spin } from './AviveStandaloneStatus';

const Text = styled(BaseText).attrs({
  color: 'primaryText',
})``;

const StyledAviveNotesForm = styled(StyledAviveSearchForm).attrs({
  color: 'background',
})``;

const StyledNotesForm = styled(StyledForm)`
  padding: 0;
`;

const StyledNotesHeader = styled(StyledHeader)`
  padding: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
`;

const StyledNotesGroup = styled(StyledFormGroup)`
  padding: 0 15px;
`;

const StyledNotesArea = styled(StyledTextArea)`
  height: 76px;
  border-radius: 5px;
  resize: none;
`;

const TextArea = ({
  name,
  errors,
  touched,
  onEnterPress = () => {},
  ...rest
}) => {
  const ref = useRef();
  useEffect(() => {
    if (ref && ref.current) {
      ref.current.focus();
    }
  }, []);
  const [field] = useField(name);

  const handleKeyDown = event => {
    if (event.keyCode == 13 && event.shiftKey == false) {
      event.preventDefault();
      onEnterPress();
    }
  };

  return (
    <>
      <StyledNotesArea
        {...field}
        {...rest}
        error={errors[name] && touched[name]}
        ref={ref}
        onKeyDown={handleKeyDown}
      />
      <small>
        <ErrorMessage name={name} />
      </small>
    </>
  );
};

const notesSchema = Yup.object().shape({
  notes: Yup.string()
    .trim()
    .defined()
    .min(1, 'Notes cannot be blank')
    .max(500, 'Notes cannot exceed 500 characters'),
});

const AviveNotesForm = ({ onClose = () => {}, onSubmit = () => {} }) => {
  const currentPhone = useSelector(state => state.avive?.currentPhone);
  const formRef = useRef();

  const handleClose = () => {
    onClose();
  };

  const submitForm = () => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const handleSubmit = (values, actions) => {
    actions.setSubmitting(true);
    onSubmit({ ...values, phone: currentPhone }, (hasError = false) => {
      if (!hasError) {
        actions.resetForm();
      }
      actions.setSubmitting(false);
    });
  };

  return (
    <StyledAviveNotesForm>
      <Formik
        innerRef={formRef}
        initialValues={{
          notes: '',
        }}
        validationSchema={notesSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isValid, dirty, isSubmitting }) => {
          const disabled = !(isValid && dirty) || isSubmitting;
          return (
            <>
              <StyledNotesHeader
                horizontal
                align="center"
                justify="space-between"
              >
                <Box>
                  <Text fontWeight="bold" size="normal" color="primaryText">
                    Send New Note for {currentPhone}
                  </Text>
                  <Text size="small">to Avive AED and App Users</Text>
                </Box>
              </StyledNotesHeader>
              <StyledNotesForm id="avive-form" data-name="avive-form">
                <StyledNotesGroup>
                  <Box mt={1}>
                    <TextArea
                      name="notes"
                      placeholder="Add note"
                      errors={errors}
                      touched={touched}
                      onEnterPress={submitForm}
                    />
                  </Box>
                </StyledNotesGroup>
              </StyledNotesForm>
              <ButtonBox horizontal align="center" justify="center">
                <Button
                  id="avive-cancel-button"
                  data-name="avive-cancel-button"
                  type="button"
                  btnType="secondary"
                  onClick={handleClose}
                  disabled={disabled}
                >
                  Cancel
                </Button>
                <Button type="submit" form="avive-form" disabled={disabled}>
                  {isSubmitting ? (
                    <>
                      Sending
                      <Box ml="5px">
                        <Spin>
                          <AviveLoadingIcon height="12" />
                        </Spin>
                      </Box>
                    </>
                  ) : (
                    <>
                      Send Note
                      <Box ml="5px">
                        <PaperPlaneIcon width="10" height="10" />
                      </Box>
                    </>
                  )}
                </Button>
              </ButtonBox>
            </>
          );
        }}
      </Formik>
    </StyledAviveNotesForm>
  );
};

export default AviveNotesForm;
